import React from 'react'
import {
  NarrativeBlock as NarrativeBlockData,
  NarrativeSectionKey,
} from '@marketing-milk/interfaces'
import { ContentEditableInput } from 'components/mentions/ContentEditableInput'
import { MentionSuggestions } from 'components/mentions/MentionSuggestions'
import { SurfacePalette, type } from '../../tokens'
import { NarrativeBlock } from '../../components/NarrativeBlock'
import { useNarrativeBlockSave } from './useNarrativeBlockSave'
import './overview-editing.css'

interface Props {
  palette: SurfacePalette
  sectionKey: NarrativeSectionKey
  label: string
  packetID: number
  narrative: NarrativeBlockData[]
  placeholder?: string
  /** Repaints the input's frame and type for a host that isn't the light editor. */
  inputStyle?: React.CSSProperties
  /** Show the overview as prose until hovered or focused. */
  quiet?: boolean
}

/**
 * The narrative block editor without any section chrome. `NarrativeSection`
 * wraps it in a `SectionCard`; the Creative section hosts it bare as the
 * strategy intro above the theme groups.
 */
export const NarrativeEditor = ({
  palette,
  sectionKey,
  label,
  packetID,
  narrative,
  placeholder,
  inputStyle,
  quiet = false,
}: Props) => {
  const {
    text,
    setText,
    setCursorPos,
    controlledCursorPos,
    setControlledCursorPos,
    mentionState,
    indicator,
    wrapperRef,
  } = useNarrativeBlockSave(packetID, sectionKey, narrative)

  const previousIndicator = React.useRef(indicator)
  const [recentlySaved, setRecentlySaved] = React.useState(false)
  React.useEffect(() => {
    const justSaved =
      indicator === 'Saved' &&
      (previousIndicator.current === 'Saving…' || previousIndicator.current === 'Unsaved')
    previousIndicator.current = indicator
    setRecentlySaved(justSaved)
    if (justSaved) {
      const timeout = setTimeout(() => setRecentlySaved(false), 2000)
      return () => clearTimeout(timeout)
    }
  }, [indicator])

  const {
    isShowingSuggestions,
    filteredUsers,
    selectedIndex,
    selectUser,
    handleKeyDown: handleMentionKeyDown,
    taggedUsers,
  } = mentionState

  return (
    <NarrativeBlock palette={palette}>
      <div ref={wrapperRef} style={{ position: 'relative' }}>
        {isShowingSuggestions && (
          <MentionSuggestions
            users={filteredUsers}
            selectedIndex={selectedIndex}
            onSelect={u => {
              const result = selectUser(u)
              if (result) {
                setText(result.newValue)
                setControlledCursorPos(result.newCursorPosition)
              }
            }}
          />
        )}
        <ContentEditableInput
          value={text}
          onChange={setText}
          onCursorPositionChange={setCursorPos}
          cursorPosition={controlledCursorPos}
          onKeyDown={e => {
            const result = handleMentionKeyDown(e)
            if (result && typeof result === 'object') {
              setText(result.newValue)
              setControlledCursorPos(result.newCursorPosition)
            }
          }}
          placeholder={placeholder ?? `Write the ${label} update…`}
          taggedUsers={taggedUsers}
          ariaLabel={label}
          className={quiet ? 'packet-overview-input' : undefined}
          style={
            {
              ...inputStyle,
              '--overview-border': palette.hairline,
              '--overview-focus': palette.accent,
            } as React.CSSProperties
          }
        />
      </div>
      <div
        role="status"
        style={{
          marginTop: 6,
          fontFamily: type.family.mono,
          fontSize: 11,
          color: palette.textFaint,
          minHeight: 14,
        }}
      >
        {quiet && indicator === 'Saved' && !recentlySaved ? '' : indicator}
      </div>
    </NarrativeBlock>
  )
}
